// exposure.ts — the exposure time series of every capture (not only flash
// lighting, §16.1): the camera track's exposure settings polled every
// EXPOSURE_POLL_MS and recorded when they change or EXPOSURE_RECORD_MS
// passed, then uploaded in batches to POST /api/scans/{id}/exposure. The
// joint trajectory solver uses exposure time to size motion blur per frame
// and to undo auto-exposure brightness steps. Passive: it never changes a
// camera setting. Browsers whose getSettings() has no exposure fields (iOS
// Safari) record nothing.

import type { ExposureSample } from '../api';
import { browserClock, toExposureSample, type FlashClock, type FlashTrackLike } from './flash';

export const EXPOSURE_POLL_MS = 250;
export const EXPOSURE_RECORD_MS = 1000;
export const EXPOSURE_UPLOAD_EVERY_MS = 5000;
// EXPOSURE_BUFFER_CAP bounds samples waiting for an upload (~80 min at 1 Hz).
export const EXPOSURE_BUFFER_CAP = 5000;

// TimedExposure is one recorded sample; t is performance.now() ms (the
// keyframe timestamp_ms / IMU sample clock).
export interface TimedExposure extends ExposureSample {
  t: number;
}

export interface ExposureLogApi {
  postExposure(id: string, samples: TimedExposure[]): Promise<void>;
}

// hasExposureFields reports whether a sample carries anything beyond the
// lock flag.
export function hasExposureFields(s: ExposureSample): boolean {
  return Object.keys(s).some((k) => k !== 'locked');
}

function sameSettings(a: ExposureSample, b: ExposureSample): boolean {
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  const ra = a as unknown as Record<string, unknown>, rb = b as unknown as Record<string, unknown>;
  return ka.every((k) => ra[k] === rb[k]);
}

export class ExposureLog {
  private pending: TimedExposure[] = [];
  private last: ExposureSample | null = null;
  private lastT = -Infinity;
  private poll: unknown = null;
  private upload: unknown = null;
  private chain: Promise<void> = Promise.resolve();
  private recorded = 0;
  private sent = 0;
  private warnedEmpty = false;

  constructor(
    private scanId: string,
    private track: FlashTrackLike | null,
    private locked: () => boolean,
    private api: ExposureLogApi,
    private clock: FlashClock = browserClock,
  ) {}

  get recordedCount(): number {
    return this.recorded;
  }

  start(): void {
    if (this.poll !== null || !this.track?.getSettings) return;
    this.sample();
    this.poll = this.clock.every(() => this.sample(), EXPOSURE_POLL_MS);
    this.upload = this.clock.every(() => void this.flush(), EXPOSURE_UPLOAD_EVERY_MS);
  }

  // sample records the current settings when they changed or a record is due.
  sample(): void {
    let raw: Record<string, unknown>;
    try {
      raw = (this.track?.getSettings?.() ?? {}) as Record<string, unknown>;
    } catch {
      return;
    }
    const s = toExposureSample(raw, this.locked());
    if (!hasExposureFields(s)) {
      if (!this.warnedEmpty) console.info('[openscan/exposure] camera settings carry no exposure fields; no exposure log', Object.keys(raw));
      this.warnedEmpty = true;
      return;
    }
    const t = this.clock.now();
    if (this.last && sameSettings(s, this.last) && t - this.lastT < EXPOSURE_RECORD_MS) return;
    this.last = s;
    this.lastT = t;
    this.pending.push({ t, ...s });
    this.recorded++;
    if (this.pending.length > EXPOSURE_BUFFER_CAP) this.pending.splice(0, this.pending.length - EXPOSURE_BUFFER_CAP);
  }

  // flush uploads pending samples (serialized with any upload in flight).
  flush(): Promise<void> {
    this.chain = this.chain.then(async () => {
      if (!this.pending.length) return;
      const batch = this.pending;
      this.pending = [];
      try {
        await this.api.postExposure(this.scanId, batch);
        this.sent += batch.length;
      } catch (err) {
        this.pending = batch.concat(this.pending).slice(-EXPOSURE_BUFFER_CAP);
        console.warn(`[openscan/exposure] upload of ${batch.length} samples failed; retrying later`, err);
      }
    });
    return this.chain;
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  // stop stops polling and the upload timer (pending samples stay queued).
  stop(): void {
    if (this.poll !== null) this.clock.cancel(this.poll);
    if (this.upload !== null) this.clock.cancel(this.upload);
    this.poll = this.upload = null;
  }

  // close stops polling and sends the rest once.
  async close(): Promise<void> {
    this.stop();
    await this.flush();
    if (this.recorded) console.info(`[openscan/exposure] ${this.recorded} samples recorded, ${this.sent} uploaded`);
  }
}
